import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as Select from '@radix-ui/react-select';
import { useFamilyStore } from '@/hooks/useStores';
import PetOwnersEditor from '@/components/pet/PetOwnersEditor';
import { PET_CATEGORY_VALUES, PET_SUBTYPE_LABEL, type PetCategoryType } from '@/types/pet-category';
import { PET_SEX_VALUES } from '@/types/pet-sex';
import { petCategory } from '@/translations/en';
import { formatDateForInput } from '@/utils/dateFormatting';
import type { DatePrecision } from '@/utils/dateFormatting';
import DateFieldWithPrecision from '@/components/shared/DateFieldWithPrecision';
import type { PetResponse } from 'we-tree-api-client/v2';
import '@/components/person/person-edit-form.css';
import '@/screens/family-member/add-pet-screen.css';

interface PetEditFormProps {
  petData: PetResponse;
  onSave: (updatedData: PetResponse) => void;
  onCancel: () => void;
  onOwnersChanged?: () => void;
}

const PetEditForm: React.FC<PetEditFormProps> = ({ petData, onSave, onCancel, onOwnersChanged }) => {
  const { t } = useTranslation('forms');
  const { t: tCommon } = useTranslation('common');
  const familyStore = useFamilyStore();
  const pet = petData;
  const [formData, setFormData] = useState({
    displayName: petData.displayName,
    category: pet.category ?? 'other',
    subType: pet.subType ?? '',
    sex: petData.sex,
    birthDate: formatDateForInput(petData.birthDate) ?? '',
    birthDatePrecision: (petData.birthDatePrecision ?? 'day') as DatePrecision,
    adoptionDate: formatDateForInput(petData.adoptionDate) ?? '',
    adoptionDatePrecision: (petData.adoptionDatePrecision ?? 'day') as DatePrecision,
    deathDate: formatDateForInput(petData.deathDate) ?? '',
    deathDatePrecision: (petData.deathDatePrecision ?? 'day') as DatePrecision,
    isDeceased: petData.livingStatus === 'deceased'
  });

  // Default form to pet's values when opening edit for a (possibly different) pet.
  // We only depend on familyMemberId so we don't overwrite the user's edits when parent re-renders.
  useEffect(() => {
    setFormData({
      displayName: petData.displayName,
      category: petData.category ?? 'other',
      subType: petData.subType ?? '',
      sex: petData.sex,
      birthDate: formatDateForInput(petData.birthDate) ?? '',
      birthDatePrecision: (petData.birthDatePrecision ?? 'day') as DatePrecision,
      adoptionDate: formatDateForInput(petData.adoptionDate) ?? '',
      adoptionDatePrecision: (petData.adoptionDatePrecision ?? 'day') as DatePrecision,
      deathDate: formatDateForInput(petData.deathDate) ?? '',
      deathDatePrecision: (petData.deathDatePrecision ?? 'day') as DatePrecision,
      isDeceased: petData.livingStatus === 'deceased'
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only reset when switching to a different pet
  }, [petData.familyMemberId]);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.displayName?.trim()) {
      newErrors.displayName = t('pet.nameRequired');
    }
    if (formData.deathDate?.trim() && !formData.isDeceased) {
      newErrors.deathDate = t('pet.markDeceasedForDeathDate');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validate()) return;

    setIsSubmitting(true);

    try {
      const birthDateVal = formData.birthDate?.trim() || undefined;
      const adoptionDateVal = formData.adoptionDate?.trim() || undefined;
      const deathDateVal = formData.isDeceased ? formData.deathDate?.trim() || undefined : undefined;

      const updateRequest: Record<string, unknown> = {
        displayName: formData.displayName.trim(),
        category: formData.category,
        subType: formData.subType?.trim() || undefined,
        sex: formData.sex,
        birthDate: birthDateVal,
        birthDatePrecision: birthDateVal ? formData.birthDatePrecision : undefined,
        adoptionDate: adoptionDateVal,
        adoptionDatePrecision: adoptionDateVal ? formData.adoptionDatePrecision : undefined,
        deathDate: deathDateVal,
        deathDatePrecision: deathDateVal ? formData.deathDatePrecision : undefined,
        livingStatus: formData.isDeceased ? 'deceased' : 'living'
      };

      // Omit undefined so keys don't appear (TSOA rejects null). Include date fields only when present.
      const filteredRequest = Object.fromEntries(
        Object.entries(updateRequest).filter(([, v]) => v !== undefined)
      ) as import('we-tree-api-client/v2').UpdatePetRequest;

      const result = await familyStore.updatePet(petData.familyMemberId, filteredRequest);

      if (result.success && result.data) {
        onSave(result.data);
      } else if (!result.success) {
        setSubmitError(result.error?.getUserMessage?.() ?? t('pet.updateFailedRetry'));
      }
    } catch {
      setSubmitError(t('pet.updateFailedRetry'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="person-edit-form">
      <div className="person-edit-form__header">
        <h2 className="person-edit-form__title">{t('pet.editTitle')}</h2>
        <div className="person-edit-form__header-actions">
          <button type="button" onClick={onCancel} className="person-edit-form__cancel-button" disabled={isSubmitting}>
            {tCommon('cancel')}
          </button>
          <button type="submit" disabled={isSubmitting} className="person-edit-form__submit-button">
            {isSubmitting ? tCommon('saving') : t('actions.save')}
          </button>
        </div>
      </div>

      {submitError && (
        <div className="person-edit-form__error" role="alert">
          <span>{submitError}</span>
          <button
            type="button"
            onClick={() => setSubmitError(null)}
            className="person-edit-form__error-dismiss"
            aria-label={t('actions.dismiss')}
          >
            ×
          </button>
        </div>
      )}

      <div className="add-pet-screen__form">
        <div className="add-pet-screen__form-row">
          <div className="person-edit-form__field">
            <label className="person-edit-form__label" htmlFor="pet-edit-displayName">
              Pet name <span className="person-edit-form__required">*</span>
            </label>
            <input
              id="pet-edit-displayName"
              type="text"
              value={formData.displayName}
              onChange={(e) => {
                setFormData((p) => ({ ...p, displayName: e.target.value }));
                if (errors.displayName) setErrors((prev) => ({ ...prev, displayName: '' }));
              }}
              disabled={isSubmitting}
              className="person-edit-form__input"
              placeholder={t('pet.namePlaceholder')}
            />
            {errors.displayName && <span className="person-edit-form__error-message">{errors.displayName}</span>}
          </div>
          <div className="person-edit-form__field">
            <label className="person-edit-form__label" htmlFor="pet-edit-category">
              {t('pet.category')} <span className="person-edit-form__required">*</span>
            </label>
            <Select.Root
              value={formData.category}
              onValueChange={(v) =>
                setFormData((p) => ({
                  ...p,
                  category: v as (typeof PET_CATEGORY_VALUES)[number]
                }))
              }
              disabled={isSubmitting}
            >
              <Select.Trigger id="pet-edit-category" className="form-select-trigger" aria-label={t('pet.categoryAria')}>
                <Select.Value />
              </Select.Trigger>
              <Select.Portal>
                <Select.Content className="form-select-content" position="popper" sideOffset={4}>
                  {PET_CATEGORY_VALUES.map((c) => (
                    <Select.Item key={c} value={c} className="form-select-item">
                      <Select.ItemText>{petCategory[c] ?? c}</Select.ItemText>
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Portal>
            </Select.Root>
          </div>
        </div>
        <div className="add-pet-screen__form-row">
          <div className="person-edit-form__field">
            <label className="person-edit-form__label" htmlFor="pet-edit-subType">
              {PET_SUBTYPE_LABEL[formData.category as PetCategoryType]}
            </label>
            <input
              id="pet-edit-subType"
              type="text"
              value={formData.subType}
              onChange={(e) => setFormData((p) => ({ ...p, subType: e.target.value }))}
              disabled={isSubmitting}
              className="person-edit-form__input"
              placeholder={
                ['dog', 'cat'].includes(formData.category)
                  ? 'e.g. Golden Retriever'
                  : ['aquatic'].includes(formData.category)
                    ? 'e.g. Goldfish, Betta'
                    : 'e.g. Hamster, Parakeet'
              }
            />
          </div>
          <div className="person-edit-form__field">
            <label className="person-edit-form__label" htmlFor="pet-edit-sex">
              {t('pet.sex')}
            </label>
            <Select.Root
              value={formData.sex}
              onValueChange={(v) =>
                setFormData((p) => ({
                  ...p,
                  sex: v as (typeof PET_SEX_VALUES)[number]
                }))
              }
              disabled={isSubmitting}
            >
              <Select.Trigger id="pet-edit-sex" className="form-select-trigger" aria-label={t('pet.sex')}>
                <Select.Value />
              </Select.Trigger>
              <Select.Portal>
                <Select.Content className="form-select-content" position="popper" sideOffset={4}>
                  {PET_SEX_VALUES.map((s) => (
                    <Select.Item key={s} value={s} className="form-select-item">
                      <Select.ItemText>{t(`petSex.${s}`)}</Select.ItemText>
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Portal>
            </Select.Root>
          </div>
        </div>
        <div className="add-pet-screen__form-row add-pet-screen__dates-row">
          <div className="person-edit-form__field">
            <DateFieldWithPrecision
              id="pet-edit-birthDate"
              label={t('pet.birthDate')}
              value={formData.birthDate}
              precision={formData.birthDatePrecision}
              onValueChange={(v) => setFormData((p) => ({ ...p, birthDate: v }))}
              onPrecisionChange={(p) => setFormData((prev) => ({ ...prev, birthDatePrecision: p }))}
              disabled={isSubmitting}
              allowClearViaInput
              layout="inline"
              wrapperClassName="add-pet-screen__date-field"
            />
          </div>
          <div className="person-edit-form__field">
            <DateFieldWithPrecision
              id="pet-edit-adoptionDate"
              label={t('pet.adoptionDate')}
              value={formData.adoptionDate}
              precision={formData.adoptionDatePrecision}
              onValueChange={(v) => setFormData((p) => ({ ...p, adoptionDate: v }))}
              onPrecisionChange={(p) => setFormData((prev) => ({ ...prev, adoptionDatePrecision: p }))}
              disabled={isSubmitting}
              allowClearViaInput
              layout="inline"
              wrapperClassName="add-pet-screen__date-field"
            />
          </div>
        </div>
        <div className="add-pet-screen__death-section">
          <div className="add-pet-screen__deceased-row">
            <label
              className="person-edit-form__label person-edit-form__checkbox-label add-pet-screen__deceased-label"
              htmlFor="pet-edit-deceased"
            >
              <input
                id="pet-edit-deceased"
                type="checkbox"
                checked={formData.isDeceased}
                onChange={(e) =>
                  setFormData((p) => ({
                    ...p,
                    isDeceased: e.target.checked,
                    ...(e.target.checked ? {} : { deathDate: '' })
                  }))
                }
                disabled={isSubmitting}
                className="person-edit-form__checkbox"
              />
              <span>{t('pet.deceased')}</span>
            </label>
          </div>
          {formData.isDeceased && (
            <div className="add-pet-screen__form-row add-pet-screen__death-date-row">
              <div className="person-edit-form__field">
                <DateFieldWithPrecision
                  id="pet-edit-deathDate"
                  label={t('pet.deathDate')}
                  value={formData.deathDate}
                  precision={formData.deathDatePrecision}
                  onValueChange={(v) => {
                    setFormData((p) => ({ ...p, deathDate: v }));
                    if (errors.deathDate) setErrors((prev) => ({ ...prev, deathDate: '' }));
                  }}
                  onPrecisionChange={(p) => setFormData((prev) => ({ ...prev, deathDatePrecision: p }))}
                  onErrorClear={() => setErrors((prev) => ({ ...prev, deathDate: '' }))}
                  disabled={isSubmitting}
                  error={errors.deathDate}
                  allowClearViaInput
                  layout="inline"
                  wrapperClassName="add-pet-screen__date-field"
                />
              </div>
            </div>
          )}
        </div>

        {onOwnersChanged && (
          <PetOwnersEditor
            petFamilyMemberId={petData.familyMemberId}
            owners={petData.owners ?? []}
            disabled={isSubmitting}
            onOwnersChanged={onOwnersChanged}
            petDeathDate={formData.isDeceased ? formData.deathDate?.trim() || undefined : undefined}
          />
        )}
      </div>

      <div className="person-edit-form__actions">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="person-edit-form__button person-edit-form__button--cancel"
        >
          {tCommon('cancel')}
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="person-edit-form__button person-edit-form__button--submit"
        >
          {isSubmitting ? tCommon('saving') : t('actions.save')}
        </button>
      </div>
    </form>
  );
};

export default PetEditForm;
